import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Base64Tool from '../components/Base64Tool';
import CodeBlock from '../components/CodeBlock';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I Base64 encode an SVG?', a: 'Paste your SVG markup above and switch to Encode. Because SVG is plain XML text, it Base64 encodes the same way any other text does — no binary image conversion is needed.' },
  { q: 'How do I use a Base64 SVG in CSS or HTML?', a: 'Wrap the Base64 output in a data URI: background-image: url("data:image/svg+xml;base64,PASTE_HERE") in CSS, or <img src="data:image/svg+xml;base64,PASTE_HERE"> in HTML.' },
  { q: 'Should I URL-encode or Base64 encode my SVG data URI?', a: 'Either works. URL-encoding (percent-encoding) usually produces a shorter, more compressible data URI for SVG since it stays mostly readable text; Base64 is simpler when the SVG already needs to travel as an opaque blob (e.g. inside JSON).' },
  { q: 'Does Base64 encoding affect SVG file size?', a: 'Yes — Base64 adds about 33% overhead versus the raw SVG text. For large icon sets, consider an SVG sprite or <symbol> reuse instead of inlining every icon as Base64.' },
];

export default function SvgToBase64Page() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="SVG to Base64 — Encode SVG for CSS & HTML | EasyBase64"
        description="Encode SVG markup to Base64 and decode it back online for free. Get a ready-to-use data URI for CSS background-image or an <img> tag."
        path="/svg-to-base64"
        jsonLd={[
          softwareApplicationSchema({ name: 'SVG to Base64 Converter', description: 'Encode and decode SVG markup as Base64 in the browser.', path: '/svg-to-base64' }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'SVG to Base64', path: '/svg-to-base64' }]),
        ]}
      />
      <PageHero
        title="SVG to Base64"
        subtitle={<>Encode SVG markup to Base64 for CSS/HTML data URIs. Working with raster images instead? Try <Link to="/image-to-base64" className="underline">Image to Base64</Link>.</>}
      />
      <div className="mb-12">
        <Base64Tool initialMode="encode" variant="standard" />
      </div>
      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12 max-w-3xl mx-auto`}>
        <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>Using the data URI</h2>
        <p className={`${themeClasses.textSecondary} leading-relaxed mb-4`}>
          Once you have the Base64 string, build a data URI and drop it straight into CSS or HTML:
        </p>
        <CodeBlock
          language="CSS"
          code={`.icon {
  background-image: url("data:image/svg+xml;base64,PASTE_BASE64_HERE");
}`}
        />
      </section>
      <FaqSection title="SVG to Base64 — FAQ" faqs={FAQS} />
    </>
  );
}
